import React, { useMemo } from 'react';
import { Patient, WellbeingFactor } from '../types/patient';
import { WellbeingMeter } from './WellbeingMeter';

interface WellbeingFactorsProps {
  patient: Patient;
}

export default function WellbeingFactors({ patient }: WellbeingFactorsProps) {
  const { wellbeingFactors } = patient;

  // Calculate category averages
  const categoryScores = useMemo(() => {
    // Mental Health: Depression, Anxiety, Stress
    const mentalHealthScore =
      (wellbeingFactors.mentalHealth.depression.score +
        wellbeingFactors.mentalHealth.anxiety.score +
        wellbeingFactors.mentalHealth.stress.score) / 3;

    // Social: Support, Isolation
    const socialScore =
      (wellbeingFactors.socialFactors.support.score +
        wellbeingFactors.socialFactors.isolation.score) / 2;

    // Lifestyle: Exercise, Nutrition, Sleep, Smoking, Alcohol
    const lifestyleScore =
      (wellbeingFactors.lifestyle.exercise.score +
        wellbeingFactors.lifestyle.nutrition.score +
        wellbeingFactors.lifestyle.sleep.score +
        wellbeingFactors.lifestyle.smoking.score +
        wellbeingFactors.lifestyle.alcohol.score) / 5;

    // Environmental: Food Security, Financial Stress, Housing
    const environmentalScore =
      (wellbeingFactors.environmental.foodInsecurity.score +
        wellbeingFactors.environmental.financialStress.score +
        wellbeingFactors.environmental.housing.score) / 3;

    return {
      mentalHealth: mentalHealthScore,
      social: socialScore,
      lifestyle: lifestyleScore,
      environmental: environmentalScore,
    };
  }, [wellbeingFactors]);

  // Calculate overall score
  const overallScore = useMemo(() => {
    return (
      (categoryScores.mentalHealth +
        categoryScores.social +
        categoryScores.lifestyle +
        categoryScores.environmental) / 4
    );
  }, [categoryScores]);

  // Prepare factor data for each category
  const mentalHealthFactors = [
    { name: 'Depression', data: wellbeingFactors.mentalHealth.depression },
    { name: 'Anxiety', data: wellbeingFactors.mentalHealth.anxiety },
    { name: 'Stress', data: wellbeingFactors.mentalHealth.stress },
  ];

  const socialFactors = [
    { name: 'Social Support', data: wellbeingFactors.socialFactors.support },
    { name: 'Social Isolation', data: wellbeingFactors.socialFactors.isolation },
  ];

  const lifestyleFactors = [
    { name: 'Exercise', data: wellbeingFactors.lifestyle.exercise },
    { name: 'Nutrition', data: wellbeingFactors.lifestyle.nutrition },
    { name: 'Sleep Quality', data: wellbeingFactors.lifestyle.sleep },
    { name: 'Smoking', data: wellbeingFactors.lifestyle.smoking },
    { name: 'Alcohol Use', data: wellbeingFactors.lifestyle.alcohol },
  ];

  const environmentalFactors = [
    { name: 'Food Security', data: wellbeingFactors.environmental.foodInsecurity },
    { name: 'Financial Stress', data: wellbeingFactors.environmental.financialStress },
    { name: 'Housing Stability', data: wellbeingFactors.environmental.housing },
  ];

  const getOverallColorClass = (score: number): string => {
    if (score <= 4) return 'text-red-700';
    if (score <= 8) return 'text-yellow-700';
    return 'text-green-700';
  };

  const getOverallBgClass = (score: number): string => {
    if (score <= 4) return 'bg-red-50 border-red-200';
    if (score <= 8) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  return (
    <div className="space-y-6">
      {/* Overall Wellbeing Score */}
      <div className={`card border-2 ${getOverallBgClass(overallScore)}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Overall Wellbeing Score
            </h3>
            <p className="text-sm text-gray-600">
              Average across all wellbeing categories
            </p>
          </div>
          <div className="text-center">
            <div className={`text-4xl font-bold ${getOverallColorClass(overallScore)}`}>
              {overallScore.toFixed(1)}
              <span className="text-2xl text-gray-500">/10</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {overallScore >= 9
                ? 'Healthy'
                : overallScore >= 5
                ? 'Moderate'
                : 'Needs Attention'}
            </div>
          </div>
        </div>
      </div>

      {/* Category Meters */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Wellbeing Categories
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WellbeingMeter
            categoryName="Mental Health"
            categoryScore={categoryScores.mentalHealth}
            factors={mentalHealthFactors}
          />

          <WellbeingMeter
            categoryName="Social Wellbeing"
            categoryScore={categoryScores.social}
            factors={socialFactors}
          />

          <WellbeingMeter
            categoryName="Lifestyle"
            categoryScore={categoryScores.lifestyle}
            factors={lifestyleFactors}
          />

          <WellbeingMeter
            categoryName="Environmental Factors"
            categoryScore={categoryScores.environmental}
            factors={environmentalFactors}
          />
        </div>
      </div>
    </div>
  );
}
